// Tela de resultado da simulação. Aberta após encerrar (via `encerrarProva`)
// ou diretamente via URL /banks/:bankId/prova/:provaId/resultado.
//
// Replica o detalhamento completo do legado (prova-result-modal em
// Simulador.html ~17021): estatísticas globais, metadados/tempos (com
// tempo médio em acertos/erros e estimativa de tempo extra), abas de
// breakdown por tema/fonte/dificuldade, tabela de questões com filtros
// (Todas/Acertos/Erros/Pendentes) e modal de gabarito por questão. Inclui
// soft delete (Excluir resultado) via /bank-prova-hide.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  Award,
  Calendar,
  Check,
  CheckCircle,
  ChevronRight,
  Circle,
  Clock,
  Eye,
  Flag,
  Home,
  Hourglass,
  Loader2,
  MinusCircle,
  RotateCcw,
  Sparkles,
  Timer,
  Trash2,
  XCircle
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type EssayResponseState,
  type ProvaState,
  type QuestaoStudy
} from "./api";
import { filterEscopo, formatHMS } from "./ProvaWorkspace";
import { openProvaStartModal } from "./ProvaStartModal";
import {
  enterProvaRevisaoView,
  AiExplainBlock,
  UngradedEssaysBannerShared
} from "./ProvaRevisaoView";
import { AiMarkdownBlock, CopyAiTextButton } from "./aiMarkdown";
import {
  readAiAnalysisCache,
  writeAiAnalysisCache,
  deleteAiAnalysisCache,
  fetchAiAnalysisFromServer,
  saveAiAnalysisToServer,
  deleteAiAnalysisFromServer,
  type AiAnalysisCache
} from "./aiAnalysisCache";
import { openAlert } from "./ConfirmPromptModal";
import {
  CHIP_BASE,
  DifficultyStars,
  QuestionBadges,
  questaoHasImages,
  questaoHasTabelas
} from "./questionBadges";
import {
  lightboxImg,
  Lightbox,
  QuestionTable,
  renderBlock,
  renderInline
} from "./StudyWorkspace";

// =====================
// State + routing
// =====================

type Phase = "loading" | "error" | "ready";

interface ProvaResultState {
  bankId: string;
  provaId: string;
  phase: Phase;
  prova: ProvaState | null;
  bank: { id: string; name: string } | null;
  questoes: QuestaoStudy[];
  /** Respostas dissertativas hidratadas do bucket estudo (mesmo bucket
   *  usado pelo ProvaWorkspace). Sem isso, contadores de "respondidas"
   *  ignoram dissertativas — bug Karol 2026-05-24. */
  essayResponses: Record<string, EssayResponseState>;
  errorMsg: string | null;
}

export const provaResultView = signal<ProvaResultState | null>(null);
// Modal de gabarito por questão clicada na tabela.
const gabaritoQuestionId = signal<string | null>(null);
// Modal de confirmação do "Excluir resultado".
const deleteConfirmOpen = signal<boolean>(false);
const deletingProva = signal<boolean>(false);

const RESULT_PATH_RE = /^\/student\/banks\/([^/]+)\/prova\/([^/]+)\/resultado\/?$/;

export function enterProvaResultView(
  bankId: string,
  provaId: string,
  opts: { pushHistory?: boolean; prefilled?: ProvaState } = {}
) {
  provaResultView.value = {
    bankId,
    provaId,
    phase: opts.prefilled ? "ready" : "loading",
    prova: opts.prefilled ?? null,
    bank: null,
    questoes: [],
    essayResponses: {},
    errorMsg: null
  };
  if (opts.pushHistory !== false) {
    const url = `/student/banks/${encodeURIComponent(bankId)}/prova/${encodeURIComponent(
      provaId
    )}/resultado`;
    if (window.location.pathname !== url) {
      window.history.pushState({ provaResultId: provaId }, "", url);
    }
  }
  void load(bankId, provaId);
}

export function exitProvaResultView() {
  provaResultView.value = null;
  gabaritoQuestionId.value = null;
  deleteConfirmOpen.value = false;
}

async function load(bankId: string, provaId: string) {
  try {
    // Quando temos prefilled (acabou de encerrar), o ProvaState completo já
    // está no state — só precisamos das questões + nome do banco. Senão,
    // chamamos bank-prova-detail (que devolve snapshot completo + questões).
    const prefilled = provaResultView.value?.prova ?? null;
    if (prefilled) {
      const provaData = await api.bankProva(bankId);
      if (provaResultView.value?.provaId !== provaId) return;
      // Karol/Luís 2026-05-24: essayResponses agora vem do prefilled
      // (prova-scoped bucket, com auto-grade aplicado) em vez da bucket
      // Study compartilhada. Sem isso, ResultView ignorava as 8 essays
      // que a aluna respondeu na prova.
      const provaEssayBucket = (
        (prefilled as unknown as { essayResponses?: Record<string, any> })
          .essayResponses ?? {}
      ) as Record<string, any>;
      provaResultView.value = {
        bankId,
        provaId,
        phase: "ready",
        prova: prefilled,
        bank: { id: provaData.bank.id, name: provaData.bank.name },
        questoes: provaData.questoes,
        essayResponses:
          Object.keys(provaEssayBucket).length > 0
            ? provaEssayBucket
            : provaData.essayResponses ?? {},
        errorMsg: null
      };
      return;
    }
    const detail = await api.bankProvaDetail(bankId, provaId);
    if (provaResultView.value?.provaId !== provaId) return;
    // DEBUG temporário — investigando "tudo zerado" reportado em 2026-05-15.
    // Loga shape de respostas/escopo pra confirmar se IDs batem.
    console.log("[ProvaResultView] detail carregado:", {
      provaId,
      respostasKeys: Object.keys(detail.prova.respostas || {}).slice(0, 5),
      respostasCount: Object.keys(detail.prova.respostas || {}).length,
      escopoIds: (detail.prova.escopoQuestaoIds || []).slice(0, 5),
      escopoCount: (detail.prova.escopoQuestaoIds || []).length,
      questoesIds: detail.questoes.slice(0, 5).map((q) => q.id),
      questoesCount: detail.questoes.length,
      escopoMatch:
        detail.prova.escopoQuestaoIds && detail.prova.escopoQuestaoIds.length > 0
          ? detail.prova.escopoQuestaoIds.filter((id) =>
              detail.questoes.some((q) => q.id === id)
            ).length
          : "n/a (escopo vazio)",
      respostasMatch: Object.keys(detail.prova.respostas || {}).filter((qid) =>
        detail.questoes.some((q) => q.id === qid)
      ).length
    });
    provaResultView.value = {
      bankId,
      provaId,
      phase: "ready",
      prova: detail.prova,
      bank: { id: detail.bank.id, name: detail.bank.name },
      questoes: detail.questoes,
      essayResponses: detail.essayResponses ?? {},
      errorMsg: null
    };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    if (err instanceof ApiError && err.status === 410) {
      provaResultView.value = {
        ...provaResultView.value!,
        phase: "error",
        errorMsg: "Este resultado foi excluído."
      };
      return;
    }
    provaResultView.value = {
      ...provaResultView.value!,
      phase: "error",
      errorMsg: err instanceof Error ? err.message : String(err)
    };
  }
}

let popstateBound = false;
export function bindProvaResultPopstate() {
  if (popstateBound || typeof window === "undefined") return;
  popstateBound = true;
  window.addEventListener("popstate", () => {
    const m = RESULT_PATH_RE.exec(window.location.pathname);
    if (m) {
      const bankId = decodeURIComponent(m[1]);
      const provaId = decodeURIComponent(m[2]);
      if (
        !provaResultView.value ||
        provaResultView.value.provaId !== provaId
      ) {
        enterProvaResultView(bankId, provaId, { pushHistory: false });
      }
    } else {
      if (provaResultView.value) provaResultView.value = null;
    }
  });
}

export function restoreProvaResultFromUrl() {
  if (typeof window === "undefined") return;
  const m = RESULT_PATH_RE.exec(window.location.pathname);
  if (m) {
    const bankId = decodeURIComponent(m[1]);
    const provaId = decodeURIComponent(m[2]);
    enterProvaResultView(bankId, provaId, { pushHistory: false });
  }
}

// =====================
// Stats (computados client-side com regra unificada)
// =====================
// Anuladas SEMPRE acertam; denominador = escopo da prova.

function isAnuladaQ(q: QuestaoStudy): boolean {
  return Number(q.correta) === 0;
}

type QuestaoStatus =
  | "acerto"
  | "erro"
  | "anulada"
  | "pendente"
  | "respondida"; // essay sem nota IA (respondida mas não classificada como acerto/erro)

/** Helper de formatação: nota 0–10 com 2 casas decimais (pt-BR usa vírgula).
 *  Karol/Luís 2026-05-24: padronizamos exibição em todas as superfícies. */
export function formatNota(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toFixed(2).replace(".", ",");
}

/** Peso (notaQuestao) da questão pra agregação ponderada da nota final.
 *  Quando ausente ou inválido, peso=1 (todas iguais). Karol/Luís 2026-05-24. */
export function pesoQuestao(q: QuestaoStudy): number {
  const p = (q as { notaQuestao?: unknown }).notaQuestao;
  if (typeof p === "number" && p > 0) return p;
  return 1;
}

/** Calcula nota 0–10 de uma questão. Regras:
 *  - Anulada: 10 (sempre acerto, ignora opcao)
 *  - MC respondida: 10 se acerto, 0 se erro
 *  - MC NÃO respondida: **0** (conta como erro pra fins de nota — antes
 *    retornava null e era ignorada no agregado, distorcendo a média)
 *  - Essay simples sem texto OU não tocada: **0**
 *  - Essay simples respondida (touched + texto) com nota IA: nota IA
 *  - Essay simples respondida sem nota IA ainda: **null** (PENDING — aguarda
 *    correção pra agregar)
 *  - Composta: média ponderada dos items pelos pesos (notaQuestao || 1).
 *    Items sem resposta = 0; items pendentes de IA = pending → questão = null.
 *
 *  Fix Luís 2026-05-24: antes "0 quando não respondida" virava null e a média
 *  só pegava as respondidas — Luís perguntou se faltava somar os zeros, e
 *  sim, faltava.
 */
export function notaQuestaoFinal(
  q: QuestaoStudy,
  prova: ProvaState,
  essayResponses: Record<string, EssayResponseState> = {}
): number | null {
  if (isAnuladaQ(q)) return 10;
  const touched = new Set(
    Array.isArray(prova.essayKeysTouched) ? prova.essayKeysTouched : []
  );
  if (q.tipo === "essay") {
    const key = String(q.id);
    const st = essayResponses[key];
    const hasText = typeof st?.resposta === "string" && st.resposta.trim().length > 0;
    if (!hasText) return 0; // não respondida = 0
    if (typeof st?.nota === "number") return st.nota;
    // touched + texto mas sem nota → pending
    if (touched.has(key)) return null;
    // texto vindo de Study bucket (não tocado nesta prova) → trata como 0
    return 0;
  }
  if (Array.isArray(q.items) && q.items.length > 0) {
    let totalPeso = 0;
    let totalGanho = 0;
    let hasPending = false;
    for (let j = 0; j < q.items.length; j++) {
      const it = q.items[j];
      const peso =
        typeof (it as any).notaQuestao === "number" && (it as any).notaQuestao > 0
          ? (it as any).notaQuestao
          : 1;
      totalPeso += peso;
      const k =
        it && typeof it.id === "string" && it.id !== ""
          ? it.id
          : `${q.id}-i${j}`;
      const isEssay = it.tipo === "essay" || (it as any).tipo == null;
      if (isEssay) {
        const st = essayResponses[k];
        const hasText =
          typeof st?.resposta === "string" && st.resposta.trim().length > 0;
        if (!hasText) {
          // item sem texto → 0 (peso já contado em totalPeso)
          continue;
        }
        if (typeof st?.nota === "number") {
          totalGanho += (st.nota / 10) * peso;
        } else if (touched.has(k)) {
          // touched + texto + sem nota → questão inteira fica pending
          hasPending = true;
        }
        // texto em bucket mas não tocado → trata como 0 (peso conta)
      } else {
        const r = prova.respostas[k];
        if (!r || r.opcao == null) {
          // item MC sem resposta → 0 (peso conta)
          continue;
        }
        const corr = (Number(it.correta) || 0) - 1;
        if (it.correta === 0 || r.opcao === corr) totalGanho += peso;
      }
    }
    if (hasPending) return null;
    return totalPeso > 0 ? (totalGanho / totalPeso) * 10 : 0;
  }
  // MC simples
  const r = prova.respostas[q.id];
  if (!r || r.opcao == null) return 0; // não respondida = 0
  const corr = (Number(q.correta) || 0) - 1;
  return r.opcao === corr ? 10 : 0;
}

function statusOf(
  q: QuestaoStudy,
  prova: ProvaState,
  essayResponses: Record<string, EssayResponseState> = {}
): QuestaoStatus {
  if (isAnuladaQ(q)) return "anulada";
  const touched = new Set(
    Array.isArray(prova.essayKeysTouched) ? prova.essayKeysTouched : []
  );
  function essayStatusFor(key: string): QuestaoStatus | null {
    if (!touched.has(key)) return null;
    const st = essayResponses[key];
    const hasText = typeof st?.resposta === "string" && st.resposta.trim().length > 0;
    if (!hasText) return null;
    if (st?.nota == null) return "respondida";
    return st.nota >= 7 ? "acerto" : "erro";
  }
  if (q.tipo === "essay") {
    return essayStatusFor(String(q.id)) ?? "pendente";
  }
  if (Array.isArray(q.items) && q.items.length > 0) {
    // Composta: usa agregação ponderada dos items (média ponderada pelos
    // pesos `notaQuestao`). Se algum item está sem nota IA ainda → respondida
    // (sem classificar acerto/erro). Aplica fallback de id pros items que
    // não têm id nativo (`${qid}-i${j}`). Karol/Luís 2026-05-24: antes
    // composta SEMPRE retornava "respondida"; agora classifica direito.
    let anyTouched = false;
    let allItemsGraded = true;
    let totalPeso = 0;
    let totalGanho = 0;
    const items = q.items ?? [];
    for (let j = 0; j < items.length; j++) {
      const it = items[j];
      const peso =
        typeof it.notaQuestao === "number" && it.notaQuestao > 0
          ? it.notaQuestao
          : 1;
      totalPeso += peso;
      const k =
        it && typeof it.id === "string" && it.id !== ""
          ? it.id
          : `${q.id}-i${j}`;
      const isEssay = it.tipo === "essay" || (it as any).tipo == null;
      if (isEssay) {
        if (!touched.has(k)) {
          allItemsGraded = false;
          continue;
        }
        const st = essayResponses[k];
        const hasText = typeof st?.resposta === "string" && st.resposta.trim().length > 0;
        if (!hasText) {
          allItemsGraded = false;
          continue;
        }
        anyTouched = true;
        if (st?.nota == null) {
          allItemsGraded = false;
          continue;
        }
        totalGanho += (st.nota / 10) * peso;
      } else {
        // MC item
        const r = prova.respostas[k];
        if (!r || r.opcao == null) {
          allItemsGraded = false;
          continue;
        }
        anyTouched = true;
        const corr = (Number(it.correta) || 0) - 1;
        if (it.correta === 0 || r.opcao === corr) totalGanho += peso;
      }
    }
    // Fallback legacy (items sem id): respondida pelo qid direto
    if (!anyTouched && essayStatusFor(String(q.id)) != null) return "respondida";
    if (!anyTouched) return "pendente";
    if (!allItemsGraded) return "respondida"; // algum item pendente de grade
    const notaFinal = totalPeso > 0 ? (totalGanho / totalPeso) * 10 : 0;
    return notaFinal >= 7 ? "acerto" : "erro";
  }
  const r = prova.respostas[q.id];
  if (!r || r.opcao == null) return "pendente";
  const corr = (Number(q.correta) || 0) - 1;
  return r.opcao === corr ? "acerto" : "erro";
}

interface ResultStats {
  total: number;
  respondidas: number;
  acertosDiretos: number;
  anuladas: number;
  acertos: number;
  erros: number;
  pendentes: number;
  pctAcertoTotal: number | null;
  pctAcertoRespondidas: number | null;
  tempoTotalMs: number;
  tempoMedioRespondidasMs: number | null;
  tempoMedioAcertosMs: number | null;
  tempoMedioErrosMs: number | null;
  estimativaTempoExtraMs: number | null;
  /** Nota final da prova, escala 0–10. Média PONDERADA das notas por questão
   *  (peso = notaQuestao do payload, default 1). Não-respondidas contam como
   *  0 (mesmo peso que errar). null quando ALGUMA questão essay está com
   *  texto e ainda aguarda nota IA — nota só resolve depois do grading. */
  notaFinal: number | null;
  /** Quantas questões já têm nota resolvida (graded ou unanswered=0). */
  notasComputadas: number;
  /** Quantas questões estão pendentes de IA grade (essay com texto sem nota). */
  pendingCount: number;
}

function computeStats(
  prova: ProvaState,
  escopo: QuestaoStudy[],
  essayResponses: Record<string, EssayResponseState> = {}
): ResultStats {
  const total = escopo.length;
  // Regra: anulada SEMPRE conta como respondida (mesmo se o aluno não marcou).
  // Vide feedback_anulada_conta_respondida.md.
  // Regra essay (Karol 2026-05-24): essay só conta se a key foi TOCADA durante
  // esta prova (prova.essayKeysTouched). Sem isso, contava bucket Study mode
  // compartilhado. Composta: ANY item.id no touched OU q.id legacy.
  const touched = new Set(
    Array.isArray(prova.essayKeysTouched) ? prova.essayKeysTouched : []
  );
  function essayCounted(q: QuestaoStudy): boolean {
    // Simples: q.id touched + texto
    if (q.tipo === "essay" && touched.has(String(q.id))) {
      const t = essayResponses[q.id]?.resposta;
      if (typeof t === "string" && t.trim().length > 0) return true;
    }
    // Composta: items[].id touched + texto, OU fallback legacy via q.id
    if (Array.isArray(q.items) && q.items.length > 0) {
      for (const it of q.items) {
        const k = it && it.id ? String(it.id) : null;
        if (k && touched.has(k)) {
          const t = essayResponses[k]?.resposta;
          if (typeof t === "string" && t.trim().length > 0) return true;
        }
      }
      if (touched.has(String(q.id))) {
        const t = essayResponses[q.id]?.resposta;
        if (typeof t === "string" && t.trim().length > 0) return true;
      }
    }
    return false;
  }
  let respondidas = 0;
  let acertosDiretos = 0;
  let erros = 0;
  let anuladasCount = 0;
  let tempoAcertosMs = 0;
  let tempoErrosMs = 0;
  for (const q of escopo) {
    if (isAnuladaQ(q)) {
      anuladasCount++;
      respondidas++;
      continue;
    }
    if (q.tipo === "essay" || (Array.isArray(q.items) && q.items.length > 0)) {
      if (essayCounted(q)) {
        respondidas++;
        // Classifica como acerto/erro a partir da nota agregada (>= 7 = acerto,
        // < 7 = erro, null = parcial → não conta acerto/erro). Karol/Luís
        // 2026-05-24: antes essay nunca entrava no acerto/erro do header.
        const nota = notaQuestaoFinal(q, prova, essayResponses);
        if (nota != null) {
          // Tempo da questão composta: soma os tempoPorQuestao dos items
          // (se mapeados) — caso contrário usa o tempo do próprio q.id.
          let tQ = prova.tempoPorQuestao?.[q.id] ?? 0;
          if (Array.isArray(q.items)) {
            const itemTime = q.items.reduce(
              (acc, it, j) => {
                const k =
                  it && typeof it.id === "string" && it.id !== ""
                    ? it.id
                    : `${q.id}-i${j}`;
                return acc + (prova.tempoPorQuestao?.[k] ?? 0);
              },
              0
            );
            if (itemTime > 0) tQ = itemTime;
          }
          if (nota >= 7) {
            acertosDiretos++;
            tempoAcertosMs += tQ;
          } else {
            erros++;
            tempoErrosMs += tQ;
          }
        }
      }
      continue;
    }
    const r = prova.respostas[q.id];
    if (!r || r.opcao == null) continue;
    respondidas++;
    const corr = (Number(q.correta) || 0) - 1;
    const tQ = prova.tempoPorQuestao?.[q.id] ?? r.tempoMs ?? 0;
    if (r.opcao === corr) {
      acertosDiretos++;
      tempoAcertosMs += tQ;
    } else {
      erros++;
      tempoErrosMs += tQ;
    }
  }
  const acertos = acertosDiretos + anuladasCount;
  const pendentes = total - respondidas;
  const pctAcertoTotal =
    total > 0 ? Math.round((acertos / total) * 100) : null;
  const pctAcertoRespondidas =
    respondidas > 0 ? Math.round((acertos / respondidas) * 100) : null;
  const tempoTotalMs = prova.tempoTotalMs;
  const tempoMedioRespondidasMs =
    respondidas > 0 ? Math.round(tempoTotalMs / respondidas) : null;
  const tempoMedioAcertosMs =
    acertosDiretos > 0 ? Math.round(tempoAcertosMs / acertosDiretos) : null;
  const tempoMedioErrosMs =
    erros > 0 ? Math.round(tempoErrosMs / erros) : null;
  const estimativaTempoExtraMs =
    pendentes > 0 && tempoMedioRespondidasMs != null
      ? tempoMedioRespondidasMs * pendentes
      : null;
  // Nota final da prova: média PONDERADA das notas POR QUESTÃO usando
  // notaQuestao como peso (default=1 quando ausente). Conta TODAS as questões
  // no denominador — não-respondida vale 0 e entra na soma com peso cheio,
  // igual a errar (regra explícita Luís 2026-05-24: "não responder e errar
  // têm o mesmo peso, que é 0"). Pending IA grade adia a nota total — só
  // computa quando todas as questões já têm nota resolvida.
  let somaPonderada = 0;
  let totalPesos = 0;
  let pendingCount = 0;
  let notasComputadas = 0;
  for (const q of escopo) {
    const peso = pesoQuestao(q);
    totalPesos += peso;
    const n = notaQuestaoFinal(q, prova, essayResponses);
    if (n == null) {
      pendingCount++;
      continue;
    }
    notasComputadas++;
    somaPonderada += (n / 10) * peso;
  }
  const notaFinal =
    pendingCount === 0 && totalPesos > 0
      ? (somaPonderada / totalPesos) * 10
      : null;
  return {
    total,
    respondidas,
    acertosDiretos,
    anuladas: anuladasCount,
    acertos,
    erros,
    pendentes,
    pctAcertoTotal,
    pctAcertoRespondidas,
    tempoTotalMs,
    tempoMedioRespondidasMs,
    tempoMedioAcertosMs,
    tempoMedioErrosMs,
    estimativaTempoExtraMs,
    notaFinal,
    notasComputadas,
    pendingCount
  };
}

interface BreakdownRow {
  label: string;
  acertos: number;
  erros: number;
  pendentes: number;
  total: number;
  pct: number | null;
  tempoTotalMs: number;
  tempoMedioMs: number | null;
  /** Nota média das questões avaliadas (0-10). Null se nenhuma avaliada. */
  notaMedia: number | null;
  /** Quantas questões já têm nota — pra display "X/Y avaliadas" quando parcial. */
  notasComputadas: number;
}

function breakdownBy(
  escopo: QuestaoStudy[],
  prova: ProvaState,
  keyFn: (q: QuestaoStudy) => string | null,
  essayResponses: Record<string, EssayResponseState> = {}
): BreakdownRow[] {
  type Slot = {
    acertos: number;
    erros: number;
    pendentes: number;
    total: number;
    tempo: number;
    respondidas: number;
    somaPonderada: number;
    totalPesos: number;
    notasComputadas: number;
    pendingCount: number;
  };
  const map = new Map<string, Slot>();
  for (const q of escopo) {
    const k = keyFn(q);
    if (!k) continue;
    const slot = map.get(k) ?? {
      acertos: 0,
      erros: 0,
      pendentes: 0,
      total: 0,
      tempo: 0,
      respondidas: 0,
      somaPonderada: 0,
      totalPesos: 0,
      notasComputadas: 0,
      pendingCount: 0
    };
    const st = statusOf(q, prova, essayResponses);
    if (st === "acerto" || st === "anulada") slot.acertos++;
    else if (st === "erro") slot.erros++;
    else if (st === "respondida") {
      // Essay/composta respondida sem nota IA ainda — conta como respondida
      // mas não vai em acerto/erro. Karol/Luís 2026-05-24.
    } else slot.pendentes++;
    if (st === "acerto" || st === "erro" || st === "respondida") {
      slot.respondidas++;
      const tQ =
        prova.tempoPorQuestao?.[q.id] ?? prova.respostas[q.id]?.tempoMs ?? 0;
      slot.tempo += tQ;
    }
    // Nota ponderada (peso = notaQuestao || 1). Não-respondida = 0 nota,
    // peso entra no denominador. Pending IA = nota aguarda, peso entra
    // no denominador também mas só se nota não-null.
    const peso = pesoQuestao(q);
    slot.totalPesos += peso;
    const nota = notaQuestaoFinal(q, prova, essayResponses);
    if (nota != null) {
      slot.somaPonderada += (nota / 10) * peso;
      slot.notasComputadas++;
    } else {
      slot.pendingCount++;
    }
    slot.total++;
    map.set(k, slot);
  }
  return Array.from(map.entries())
    .map(([label, s]) => ({
      label,
      acertos: s.acertos,
      erros: s.erros,
      pendentes: s.pendentes,
      total: s.total,
      pct: s.total > 0 ? Math.round((s.acertos / s.total) * 100) : null,
      tempoTotalMs: s.tempo,
      tempoMedioMs: s.respondidas > 0 ? Math.round(s.tempo / s.respondidas) : null,
      // Nota média do tema/fonte/dif — só resolve quando todos avaliados.
      // Subline já mostra "X/Y avaliadas" no UI quando parcial.
      notaMedia:
        s.pendingCount === 0 && s.totalPesos > 0
          ? (s.somaPonderada / s.totalPesos) * 10
          : null,
      notasComputadas: s.notasComputadas
    }))
    .filter((r) => r.acertos + r.erros + r.pendentes > 0)
    .sort((a, b) => (a.pct ?? 100) - (b.pct ?? 100));
}

function motivoEncerramentoLabel(motivo?: string): string {
  switch (motivo) {
    case "tempo":
      return "Tempo esgotado";
    case "falta-respostas":
      return "Falta de respostas";
    case "manual":
      return "Encerrada pelo aluno";
    default:
      return motivo || "—";
  }
}

// =====================
// UI
// =====================

export function ProvaResultViewComp() {
  const state = provaResultView.value;
  if (!state) return null;
  if (state.phase === "loading") return <ResultLoading />;
  if (state.phase === "error") return <ResultError msg={state.errorMsg} />;
  return (
    <>
      <ResultReady state={state} />
      {gabaritoQuestionId.value && <GabaritoModal />}
      {deleteConfirmOpen.value && <DeleteConfirmModal />}
      {lightboxImg.value && <Lightbox image={lightboxImg.value} />}
    </>
  );
}

function ResultLoading() {
  return (
    <div class="space-y-5">
      <header class="flex items-center gap-3">
        <Skeleton className="size-12 rounded-md" />
        <div class="flex-1 space-y-2">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-40" />
        </div>
      </header>
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-md" />
        ))}
      </div>
      <div class="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Skeleton className="h-48 rounded-md lg:col-span-2" />
        <Skeleton className="h-48 rounded-md" />
      </div>
      <Skeleton className="h-64 rounded-md" />
    </div>
  );
}

function ResultError({ msg }: { msg: string | null }) {
  return (
    <div class="grid place-items-center py-16">
      <div class="max-w-md space-y-3 rounded-md border border-destructive/30 bg-destructive/5 px-6 py-8 text-center">
        <AlertCircle className="mx-auto size-8 text-destructive" />
        <h2 class="text-base font-bold">Não consegui carregar o resultado</h2>
        {msg && <p class="text-sm text-muted-foreground">{msg}</p>}
        <button
          type="button"
          onClick={() => {
            exitProvaResultView();
            window.history.pushState({}, "", "/student/");
            window.dispatchEvent(new PopStateEvent("popstate"));
          }}
          class="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Voltar para a home
        </button>
      </div>
    </div>
  );
}

function ResultReady({ state }: { state: ProvaResultState }) {
  // Auto-retry de grading IA pra essays sem nota — cobre o caso em que o
  // auto-grade no encerramento falhou parcialmente (IA timeout, rate-limit).
  // Só dispara UMA vez por provaId via flag em localStorage (não loopar se
  // falhar de novo). Karol/Luís 2026-05-24.
  useEffect(() => {
    if (!state.prova) return;
    const bucket = state.essayResponses ?? {};
    const touched = new Set(
      Array.isArray(state.prova.essayKeysTouched)
        ? state.prova.essayKeysTouched
        : []
    );
    let ungradedComText = 0;
    for (const k of touched) {
      const ent = bucket[k];
      if (
        ent &&
        typeof ent.resposta === "string" &&
        ent.resposta.trim().length > 0 &&
        ent.nota == null
      ) {
        ungradedComText++;
      }
    }
    if (ungradedComText === 0) return;
    const flag = `prova-grade-retry-tried-${state.provaId}`;
    try {
      if (localStorage.getItem(flag)) return;
      localStorage.setItem(flag, "1");
    } catch {
      return;
    }
    console.info(
      `[result-view] ${ungradedComText} essay(s) sem nota — disparando retry IA`
    );
    void (async () => {
      try {
        const r = await api.bankProvaGradeRetry(state.bankId, state.provaId);
        console.info("[result-view] grade-retry ok:", r);
        // Recarrega prova-detail pra pegar bucket atualizado
        try {
          const fresh = await api.bankProvaDetail(state.bankId, state.provaId);
          const cur = provaResultView.value;
          if (cur && cur.provaId === state.provaId) {
            provaResultView.value = {
              ...cur,
              prova: fresh.prova,
              questoes: fresh.questoes,
              essayResponses: fresh.essayResponses ?? cur.essayResponses
            };
          }
        } catch (e) {
          console.warn("[result-view] reload after retry falhou:", e);
        }
      } catch (e) {
        console.warn("[result-view] grade-retry falhou:", e);
      }
    })();
  }, [state.bankId, state.provaId]);

  const escopo = useMemo(() => {
    if (!state.prova) return [];
    if (state.prova.escopoQuestaoIds.length === 0) return state.questoes;
    return filterEscopo(state.questoes, state.prova.escopoQuestaoIds);
  }, [state.questoes, state.prova]);
  const stats = useMemo(() => {
    if (!state.prova) return null;
    return computeStats(state.prova, escopo, state.essayResponses);
  }, [state.prova, escopo, state.essayResponses]);
  const temas = useMemo(() => {
    if (!state.prova) return [];
    return breakdownBy(escopo, state.prova, (q) => q.tema ?? null);
  }, [state.prova, escopo]);
  const fontes = useMemo(() => {
    if (!state.prova) return [];
    return breakdownBy(escopo, state.prova, (q) => q.fonte ?? null);
  }, [state.prova, escopo]);
  const dif = useMemo(() => {
    if (!state.prova) return [];
    return breakdownBy(escopo, state.prova, (q) =>
      q.dificuldade ? `Nível ${q.dificuldade}` : null
    );
  }, [state.prova, escopo]);

  if (!stats || !state.prova) return null;

  return (
    <div class="space-y-6">
      <ResultHeader state={state} stats={stats} />
      <UngradedEssaysBannerShared
        prova={state.prova}
        essayResponses={state.essayResponses}
        bankId={state.bankId}
        provaId={state.provaId}
        onAfterRetry={(fresh) => {
          const cur = provaResultView.value;
          if (cur && cur.provaId === state.provaId) {
            provaResultView.value = {
              ...cur,
              prova: fresh.prova,
              questoes: fresh.questoes,
              essayResponses: fresh.essayResponses ?? cur.essayResponses
            };
          }
        }}
      />
      <ResultStatsGrid stats={stats} />
      <ResultMetaAndTimes state={state} stats={stats} />
      <AiAnalysisBlock state={state} stats={stats} escopo={escopo} />
      <ResultBreakdownTabs temas={temas} fontes={fontes} dif={dif} />
      <ResultQuestionsTable state={state} escopo={escopo} />
      <ResultFooter state={state} />
    </div>
  );
}

// =====================
// Análise da IA (pós-prova)
// =====================
// Gera uma avaliação textual do desempenho — pontos fortes, fracos, próximos
// passos. Persiste no localStorage (chaveada por provaId) pra não regerar
// toda vez que o aluno abrir o resultado. Quando o backend tiver endpoint
// dedicado (`/api/student/assessment-evaluation`), migra a persistência pra lá.

function AiAnalysisBlock({
  state,
  stats,
  escopo
}: {
  state: ProvaResultState;
  stats: ResultStats;
  escopo: QuestaoStudy[];
}) {
  const [cache, setCache] = useState<AiAnalysisCache | null>(() =>
    readAiAnalysisCache(state.provaId)
  );
  const [phase, setPhase] = useState<"idle" | "loading" | "error">("idle");
  const [err, setErr] = useState<string | null>(null);

  // Hidrata do server quando o componente monta — cobre o caso de o aluno
  // ter gerado a avaliação em outro device. Localstorage permanece como
  // fonte rápida; server overrides se mais recente.
  // Após hidratar, se ainda não houver cache, AUTO-GERA uma única vez por
  // prova (flag ai-analysis-tried-${provaId}) — fluxo "prova encerrada → IA
  // já analisou sozinha quando o aluno abrir o resultado".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const serverCache = await fetchAiAnalysisFromServer(
        state.bankId,
        state.provaId
      );
      if (cancelled) return;
      if (serverCache) {
        const local = readAiAnalysisCache(state.provaId);
        if (
          !local ||
          new Date(serverCache.generatedAt).getTime() >
            new Date(local.generatedAt).getTime()
        ) {
          writeAiAnalysisCache(state.provaId, serverCache);
          setCache(serverCache);
        }
        return;
      }
      // Sem cache no server. Verifica se já temos local; se sim, fim.
      const local = readAiAnalysisCache(state.provaId);
      if (local) return;
      // Sem cache local nem server. Auto-gera UMA VEZ por prova — flag
      // garante que se a IA falhar (rate-limit, sem chave), não fica loop.
      const triedKey = `ai-analysis-tried-${state.provaId}`;
      try {
        if (localStorage.getItem(triedKey)) return;
        localStorage.setItem(triedKey, "1");
      } catch {
        return;
      }
      // Só auto-gera se a prova foi efetivamente respondida (caso contrário
      // a IA não tem o que analisar — aluno deixou em branco e veio direto
      // pro resultado). Limiar: >= 1 respondida.
      if (stats.respondidas < 1) return;
      void generate();
    })();
    return () => {
      cancelled = true;
    };
  }, [state.bankId, state.provaId]);

  async function generate() {
    if (!state.prova) return;
    setPhase("loading");
    setErr(null);
    try {
      // Monta um resumo da performance pra IA processar. Inclui:
      // - score global
      // - desempenho TOTAL por tema (não só top/bottom 3 — IA precisa do mapa
      //   inteiro pra recomendar conceitos específicos por área fraca)
      // - tempo médio + por acerto/erro
      // - tempo MÉDIO POR TEMA (acertos vs erros) — pra IA falar sobre
      //   "errou rápido" vs "demorou e errou" vs "demorou pra acertar"
      // - desempenho por DIFICULDADE (1-5, se o banco categoriza)
      // - amostra de questões erradas (até 6) com tema e enunciado curto pra
      //   IA conseguir apontar conceitos concretos em vez de generalidades
      const prova = state.prova;
      const porTema = breakdownBy(escopo, prova, (q) => q.tema ?? null);
      const respondidasDe = (b: BreakdownRow) => b.acertos + b.erros;
      // Lista TODOS os temas ordenados por desempenho (pior → melhor). Inclui
      // tempo médio por tema (acertos e erros separados) pra IA correlacionar
      // ritmo × performance. Sem o tempo aqui, ela só consegue dizer "errou
      // muito em X" — com, consegue dizer "errou em X mas em tempo rápido =
      // chute" vs "demorou e errou = não dominou".
      const tempoPorQuestao = (prova.tempoPorQuestao ?? {}) as Record<
        string,
        number
      >;
      function tempoMsDaQ(qid: string): number {
        const t = tempoPorQuestao[qid];
        if (typeof t === "number" && t > 0) return t;
        const r = (prova.respostas as Record<string, { tempoMs?: number } | undefined>)[qid];
        return typeof r?.tempoMs === "number" ? r.tempoMs : 0;
      }
      const temasOrdenados = porTema
        .filter((b) => respondidasDe(b) > 0)
        .map((b) => {
          // Coleta tempos das questões deste tema, separando acertos e erros.
          const acertosMs: number[] = [];
          const errosMs: number[] = [];
          for (const q of escopo) {
            if ((q.tema ?? null) !== b.label) continue;
            const r = (prova.respostas as Record<
              string,
              { opcao?: number } | undefined
            >)[q.id];
            if (!r || r.opcao == null) continue;
            // Anulada conta como acerto, mas tempo dela pode ser zero/curto
            // — ignora pra não distorcer o cálculo de "tempo médio em acertos
            // reais".
            if (Number(q.correta) === 0) continue;
            const t = tempoMsDaQ(q.id);
            const corr = (Number(q.correta) || 0) - 1;
            if (r.opcao === corr) acertosMs.push(t);
            else errosMs.push(t);
          }
          const avg = (xs: number[]) =>
            xs.length === 0
              ? null
              : Math.round(xs.reduce((s, x) => s + x, 0) / xs.length);
          return {
            label: b.label,
            acertos: b.acertos,
            erros: b.erros,
            total: respondidasDe(b),
            pct: Math.round((b.acertos / Math.max(1, respondidasDe(b))) * 100),
            tempoMedioAcertosMs: avg(acertosMs),
            tempoMedioErrosMs: avg(errosMs)
          };
        })
        .sort((a, b) => a.pct - b.pct);

      // Desempenho por dificuldade. Banco pode não categorizar — só inclui
      // se ao menos 1 questão tem dificuldade declarada (1-5).
      const porDificuldade = new Map<
        number,
        { acertos: number; erros: number; tempos: number[] }
      >();
      let temDificuldade = false;
      for (const q of escopo) {
        const d = Number(q.dificuldade);
        if (!d || d < 1 || d > 5) continue;
        temDificuldade = true;
        const r = (prova.respostas as Record<
          string,
          { opcao?: number } | undefined
        >)[q.id];
        if (!r || r.opcao == null) continue;
        if (Number(q.correta) === 0) continue;
        if (!porDificuldade.has(d)) {
          porDificuldade.set(d, { acertos: 0, erros: 0, tempos: [] });
        }
        const slot = porDificuldade.get(d)!;
        const corr = (Number(q.correta) || 0) - 1;
        if (r.opcao === corr) slot.acertos++;
        else slot.erros++;
        slot.tempos.push(tempoMsDaQ(q.id));
      }
      const dificuldadesOrdenadas = temDificuldade
        ? [...porDificuldade.entries()]
            .sort(([a], [b]) => a - b)
            .map(([nivel, slot]) => {
              const total = slot.acertos + slot.erros;
              const tempoMedio =
                slot.tempos.length === 0
                  ? null
                  : Math.round(
                      slot.tempos.reduce((s, x) => s + x, 0) /
                        slot.tempos.length
                    );
              return {
                nivel,
                acertos: slot.acertos,
                erros: slot.erros,
                total,
                pct:
                  total > 0
                    ? Math.round((slot.acertos / total) * 100)
                    : 0,
                tempoMedioMs: tempoMedio
              };
            })
        : [];
      // Amostra de questões erradas: top 6 dos temas fracos.
      const erradasSample: Array<{ tema: string; pergunta: string }> = [];
      const respostasObj = (prova.respostas ?? {}) as Record<
        string,
        { opcao?: number } | undefined
      >;
      for (const q of escopo) {
        if (erradasSample.length >= 6) break;
        const r = respostasObj[q.id];
        if (!r || r.opcao == null) continue;
        // q.correta é 1-based; opcao é 0-based; anulada (correta=0) é acerto.
        const corr = (Number(q.correta) || 0) - 1;
        if (Number(q.correta) === 0) continue;
        if (r.opcao === corr) continue;
        const enunciadoCurto = (q.pergunta ?? "")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 220);
        if (!enunciadoCurto) continue;
        erradasSample.push({
          tema: q.tema ?? "sem-tema",
          pergunta: enunciadoCurto
        });
      }

      const fmtT = (ms: number | null): string =>
        ms == null ? "—" : formatHMS(ms);

      const resumo = [
        `Banco: ${state.bank?.name ?? "—"}`,
        `Total: ${stats.total} questões | Respondidas: ${stats.respondidas} | Acertos: ${stats.acertos} (${stats.pctAcertoTotal ?? 0}%) | Erros: ${stats.erros} | Pendentes: ${stats.pendentes}`,
        `Tempo total: ${formatHMS(stats.tempoTotalMs)} | Tempo médio respondidas: ${formatHMS(stats.tempoMedioRespondidasMs ?? 0)} | Médio acertos: ${formatHMS(stats.tempoMedioAcertosMs ?? 0)} | Médio erros: ${formatHMS(stats.tempoMedioErrosMs ?? 0)}`,
        temasOrdenados.length
          ? `Desempenho por tema (pior → melhor, com tempo médio acertos | erros):\n${temasOrdenados
              .map(
                (t) =>
                  `  - ${t.label}: ${t.acertos}/${t.total} (${t.pct}%) — tempo médio acertos ${fmtT(t.tempoMedioAcertosMs)} | erros ${fmtT(t.tempoMedioErrosMs)}`
              )
              .join("\n")}`
          : "",
        dificuldadesOrdenadas.length
          ? `Desempenho por dificuldade (1=fácil → 5=difícil, com tempo médio):\n${dificuldadesOrdenadas
              .map(
                (d) =>
                  `  - Nível ${d.nivel}: ${d.acertos}/${d.total} (${d.pct}%) — tempo médio ${fmtT(d.tempoMedioMs)}`
              )
              .join("\n")}`
          : "",
        erradasSample.length
          ? `Amostra de questões erradas (até 6, pra ancorar conceitos concretos):\n${erradasSample
              .map((e, i) => `  ${i + 1}. [${e.tema}] ${e.pergunta}`)
              .join("\n")}`
          : ""
      ]
        .filter(Boolean)
        .join("\n");

      // Reutiliza o endpoint de chat com uma questão "sintética" — o
      // backend trata como conversa, mas o prompt direciona a saída pra um
      // texto de avaliação completo. Quando tiver endpoint dedicado,
      // migra pra api.assessmentEvaluation({...}).
      const fakeQuestion = {
        id: `prova-${state.provaId}`,
        tema: "Avaliação de desempenho",
        fonte: state.bank?.name ?? null,
        pergunta: resumo,
        caso: null,
        alternativas: [],
        correta: null,
        resolucao: null
      };
      const resp = await api.aiChat({
        question: fakeQuestion as any,
        history: [],
        message:
          "Com base no resumo de desempenho acima, escreva um VEREDITO de avaliação — não é uma conversa, é uma análise fechada que o aluno vai LER e usar como referência. " +
          "ESTRUTURA OBRIGATÓRIA em 5 seções (cabeçalhos markdown idênticos a estes, NESTA ORDEM): " +
          "'## Pontos fortes', '## Pontos a melhorar', '## Tempo e padrões', '## Conceitos pra revisar' e '## Onde estudar mais'. " +
          "\n\n" +
          "Em **Pontos fortes** e **Pontos a melhorar**: análise direta do que os números mostram — temas dominados vs temas que precisam revisão, " +
          "padrões observáveis. Cite percentuais e nomes de temas do resumo. " +
          "\n\n" +
          "Em **Tempo e padrões**: análise CRUZADA entre tempo e desempenho. Use os dados de tempo médio por tema (acertos vs erros) e por dificuldade. " +
          "Aponte padrões concretos: ex. *'errou rápido em Hematologia (média 0:45) → indica chute mais do que confusão conceitual'*, " +
          "*'demorou 2 min e errou em Endócrino → conhece o tema mas tropeçou em detalhe'*, " +
          "*'foi muito rápido em questões nível 5 → pode estar pulando enunciados longos'*, " +
          "*'tempo médio em acertos baixo → ritmo bom, mantém'*. " +
          "Use bullets pra cada padrão observado (3–6 bullets). Seja específico com números do resumo — sem genericalidades. " +
          "Conclua com 1 ou 2 recomendações práticas de RITMO de estudo (ex: *'pratique leitura ativa antes de marcar em questões de nível 5'* ou *'reveja conceitos básicos de Endócrino — tempo gasto mostra dúvida, não desconhecimento'*). " +
          "\n\n" +
          "Em **Conceitos pra revisar**: liste em bullets os CONCEITOS ESPECÍFICOS (não temas vagos) que o aluno precisa estudar. " +
          "Use a amostra de questões erradas pra ancorar conceitos concretos — ex: 'mecanismo de ação dos beta-bloqueadores', " +
          "'cálculo de fluxo sanguíneo renal', 'sintaxe de orações subordinadas adverbiais'. " +
          "Seja granular. Cada bullet = 1 conceito + 1 frase explicando por que aquele conceito apareceu nos erros. " +
          "Limite a 5–8 bullets nos conceitos mais críticos. " +
          "\n\n" +
          "Em **Onde estudar mais**: recomende fontes REAIS e VERIFICÁVEIS pra cada área fraca. Use bullets agrupados por sub-tópico. Inclua: " +
          "(a) **livros acadêmicos de referência** (autor + título — só obras conhecidas da área, ex: Robbins Patologia, Goldman-Cecil Medicina Interna, Harrison, Iturricha Cálculo, Cury Português Esquematizado, etc.); " +
          "(b) **sites e bases confiáveis** (cite por nome — PubMed, SciELO, UpToDate, Khan Academy, Brasil Escola, Mundo Educação, Stoodi, etc. NÃO invente URLs específicas, só o domínio e o que buscar lá); " +
          "(c) **termos de busca específicos** que o aluno deve digitar pra achar o material correto. " +
          "Adapte a bibliografia à área das questões (medicina, direito, exatas, humanas etc.) inferida pelos temas. " +
          "\n\n" +
          "REGRAS DURAS: " +
          "NÃO invente URLs completas (só nome de site/base + termo de busca). " +
          "NÃO sugira 'gerar mais questões', 'fazer outra simulação', 'continuar o estudo na plataforma', 'me pergunte mais', 'me conte mais', 'qualquer dúvida'. " +
          "NÃO use 'estou aqui para ajudar'. " +
          "Tom de professor experiente entregando um parecer: claro, objetivo, fundamentado nos números e na amostra de erros. " +
          "Use **negrito** pra termos-chave e $...$ pra expressões matemáticas relevantes.",
        respostaAluno: null
      });
      const text = resp.reply ?? "";
      const next: AiAnalysisCache = {
        text,
        generatedAt: new Date().toISOString()
      };
      writeAiAnalysisCache(state.provaId, next);
      // Fire-and-forget pro server — não bloqueia o UI.
      void saveAiAnalysisToServer(state.bankId, state.provaId, next);
      setCache(next);
      setPhase("idle");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }

  function regenerate() {
    deleteAiAnalysisCache(state.provaId);
    void deleteAiAnalysisFromServer(state.bankId, state.provaId);
    setCache(null);
    void generate();
  }

  // Cache existente — exibe direto
  if (cache) {
    return (
      <section class="rounded-md border border-primary/40 bg-primary/5 p-5">
        <header class="mb-3 flex flex-wrap items-center gap-2">
          <Sparkles className="size-4 text-primary" />
          <h2 class="text-sm font-bold uppercase tracking-wider text-primary">
            Avaliação da IA
          </h2>
          <span class="text-[11px] text-muted-foreground">
            gerada em {new Date(cache.generatedAt).toLocaleString("pt-BR")}
          </span>
          <div class="ml-auto flex items-center gap-1.5">
            <CopyAiTextButton text={cache.text} />
            <button
              type="button"
              onClick={regenerate}
              disabled={phase === "loading"}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent disabled:opacity-50"
            >
              {phase === "loading" ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <RotateCcw className="size-3" />
              )}
              Regerar
            </button>
          </div>
        </header>
        <AiMarkdownBlock text={cache.text} />
      </section>
    );
  }

  // Sem cache — botão pra gerar
  if (phase === "idle") {
    return (
      <button
        type="button"
        onClick={generate}
        class="group flex w-full items-center gap-3 rounded-md border border-primary/40 bg-primary/5 px-5 py-4 text-left transition-colors hover:border-primary/60 hover:bg-primary/10"
      >
        <span class="grid size-11 shrink-0 place-items-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/30">
          <Sparkles className="size-5" />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-bold text-primary">
            Gerar avaliação da IA
          </span>
          <span class="block text-xs text-muted-foreground">
            Análise didática do seu desempenho — pontos fortes, áreas a revisar
            e próximos passos. Persistida pra próximas visitas.
          </span>
        </span>
        <ChevronRight className="size-5 shrink-0 text-primary/60 transition-transform group-hover:translate-x-0.5" />
      </button>
    );
  }

  if (phase === "loading") {
    return (
      <section class="rounded-md border border-primary/40 bg-primary/5 p-5">
        <div class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin text-primary" />
          A IA está analisando seu desempenho — pode levar 10-20 segundos.
        </div>
      </section>
    );
  }

  // error
  return (
    <section class="rounded-md border border-destructive/40 bg-destructive/5 p-5">
      <div class="mb-2 flex items-center gap-2 text-sm font-bold text-destructive">
        <AlertCircle className="size-4" />
        Falha ao gerar avaliação
      </div>
      <p class="mb-3 text-xs text-muted-foreground">{err}</p>
      <button
        type="button"
        onClick={() => {
          setPhase("idle");
          void generate();
        }}
        class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
      >
        Tentar novamente
      </button>
    </section>
  );
}

// =====================
// Header com data, motivo, ação Excluir
// =====================

function ResultHeader({
  state,
  stats
}: {
  state: ProvaResultState;
  stats: ResultStats;
}) {
  const prova = state.prova!;
  const inicio = prova.iniciadaEm ? new Date(prova.iniciadaEm) : null;
  const fim = prova.encerradaEm ? new Date(prova.encerradaEm) : null;
  const motivo = (prova as ProvaState & { motivoEncerramento?: string })
    .motivoEncerramento;
  return (
    <header class="flex flex-wrap items-start gap-3 rounded-md border border-border bg-card/40 px-4 py-3">
      <div class="grid size-12 shrink-0 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
        <Award className="size-6" />
      </div>
      <div class="min-w-0 flex-1">
        <h1 class="truncate text-xl font-extrabold tracking-tight">
          Resultado da simulação
        </h1>
        <p class="mt-0.5 truncate text-sm font-medium text-foreground/80">
          {state.bank?.name ?? "—"}
        </p>
        <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {inicio && (
            <span class="inline-flex items-center gap-1">
              <Calendar width="1em" height="1em" />
              Início: <strong class="font-semibold text-foreground/80">{formatDate(inicio)}</strong>
            </span>
          )}
          {fim && (
            <span class="inline-flex items-center gap-1">
              <Calendar width="1em" height="1em" />
              Fim: <strong class="font-semibold text-foreground/80">{formatDate(fim)}</strong>
            </span>
          )}
          <span class="inline-flex items-center gap-1">
            <Clock width="1em" height="1em" />
            Duração: <strong class="font-semibold text-foreground/80">{formatHMS(stats.tempoTotalMs)}</strong>
            {prova.tempoExtraMs > 0 && (
              <span class="text-amber-600 dark:text-amber-400">
                (+{formatHMS(prova.tempoExtraMs)})
              </span>
            )}
          </span>
          {motivo && (
            <span class="inline-flex items-center gap-1">
              <Flag width="1em" height="1em" />
              {motivoEncerramentoLabel(motivo)}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => (deleteConfirmOpen.value = true)}
        title="Excluir resultado"
        class="inline-flex h-9 items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/5 px-3 text-xs font-semibold text-red-600 transition-colors hover:bg-red-500/10 dark:text-red-400"
      >
        <Trash2 width="1.1em" height="1.1em" />
        <span class="hidden sm:inline">Excluir resultado</span>
      </button>
    </header>
  );
}

// =====================
// Grid de 6 cards de stats globais
// =====================

function ResultStatsGrid({ stats }: { stats: ResultStats }) {
  return (
    <section class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <StatCard
        label="Questões"
        value={stats.total}
        subline="no escopo da prova"
        tone="muted"
        icon={Circle}
      />
      <StatCard
        label="Respondidas"
        value={stats.respondidas}
        subline={
          stats.total > 0
            ? `${Math.round((stats.respondidas / stats.total) * 100)}% do total`
            : "—"
        }
        tone="primary"
        icon={Check}
      />
      <StatCard
        label="Não respondidas"
        value={stats.pendentes}
        subline={
          stats.total > 0
            ? `${Math.round((stats.pendentes / stats.total) * 100)}% do total`
            : "—"
        }
        tone="muted"
        icon={MinusCircle}
      />
      <StatCard
        label="Acertos"
        value={stats.acertos}
        subline={
          stats.anuladas > 0
            ? `inclui ${stats.anuladas} anulada${stats.anuladas > 1 ? "s" : ""}`
            : `${stats.acertosDiretos} corretas`
        }
        tone="emerald"
        icon={CheckCircle}
      />
      <StatCard
        label="Erros"
        value={stats.erros}
        subline={
          stats.respondidas > 0
            ? `${Math.round((stats.erros / stats.respondidas) * 100)}% das respondidas`
            : "—"
        }
        tone="red"
        icon={XCircle}
      />
      <StatCard
        label="Aproveitamento"
        value={stats.pctAcertoTotal != null ? `${stats.pctAcertoTotal}%` : "—"}
        subline={`sobre ${stats.total} questões`}
        tone="primary"
        icon={Award}
      />
      {/* Nota final da prova — escala 0-10 com 2 casas. Karol/Luís 2026-05-24.
          Pondera por notaQuestao (peso); não-respondida conta como 0.
          Mostra "aguardando IA" se algum essay sem nota ainda. */}
      <StatCard
        label="Nota final"
        value={stats.notaFinal != null ? formatNota(stats.notaFinal) : "—"}
        subline={
          stats.pendingCount > 0
            ? `${stats.pendingCount} aguardando IA`
            : `ponderada · 0–10`
        }
        tone="primary"
        icon={Award}
      />
    </section>
  );
}

// =====================
// Bloco de metadados + tempos + box amarelo "Estimativa tempo extra"
// =====================

function ResultMetaAndTimes({
  state,
  stats
}: {
  state: ProvaResultState;
  stats: ResultStats;
}) {
  const prova = state.prova!;
  return (
    <section class="grid grid-cols-1 gap-3 lg:grid-cols-3">
      {/* Lista de métricas de tempo */}
      <div class="space-y-1 rounded-md border border-border bg-card/40 px-4 py-3 lg:col-span-2">
        <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Tempos
        </h3>
        <ul class="divide-y divide-border/60 text-sm">
          <MetaRow
            icon={Clock}
            label="Tempo total"
            value={formatHMS(stats.tempoTotalMs)}
            hint={
              prova.tempoExtraMs > 0
                ? `+${formatHMS(prova.tempoExtraMs)} além do alvo`
                : null
            }
          />
          <MetaRow
            icon={Timer}
            label="Tempo médio por questão respondida"
            value={
              stats.tempoMedioRespondidasMs != null
                ? formatHMS(stats.tempoMedioRespondidasMs)
                : "—"
            }
            hint={
              stats.respondidas > 0
                ? `média de ${stats.respondidas} respondidas`
                : null
            }
          />
          <MetaRow
            icon={Timer}
            label="Tempo médio nos acertos"
            value={
              stats.tempoMedioAcertosMs != null
                ? formatHMS(stats.tempoMedioAcertosMs)
                : "—"
            }
            tone="emerald"
            hint={
              stats.acertosDiretos > 0
                ? `${stats.acertosDiretos} acerto${stats.acertosDiretos > 1 ? "s" : ""} contabilizado${stats.acertosDiretos > 1 ? "s" : ""}`
                : null
            }
          />
          <MetaRow
            icon={Timer}
            label="Tempo médio nos erros"
            value={
              stats.tempoMedioErrosMs != null
                ? formatHMS(stats.tempoMedioErrosMs)
                : "—"
            }
            tone="red"
            hint={
              stats.erros > 0
                ? `${stats.erros} erro${stats.erros > 1 ? "s" : ""}`
                : null
            }
          />
          {stats.pendentes > 0 && (
            <MetaRow
              icon={Award}
              label="Aproveitamento entre respondidas"
              value={
                stats.pctAcertoRespondidas != null
                  ? `${stats.pctAcertoRespondidas}%`
                  : "—"
              }
              tone="primary"
              hint={`${stats.acertos}/${stats.respondidas}`}
            />
          )}
        </ul>
      </div>

      {/* Box amarelo: estimativa de tempo extra (só se houver pendentes) */}
      {stats.pendentes > 0 && stats.estimativaTempoExtraMs != null && (
        <div class="space-y-2 rounded-md border-2 border-dashed border-amber-500/50 bg-amber-500/10 px-4 py-3 text-amber-900 dark:text-amber-200">
          <div class="flex items-center gap-2">
            <Hourglass className="size-5 text-amber-600 dark:text-amber-400" />
            <h3 class="text-xs font-bold uppercase tracking-wider">
              Estimativa de tempo extra
            </h3>
          </div>
          <div class="tabular-nums text-2xl font-extrabold leading-none">
            {formatHMS(stats.estimativaTempoExtraMs)}
          </div>
          <p class="text-[12px] leading-snug opacity-90">
            Tempo aproximado que faltaria pra concluir{" "}
            {stats.pendentes > 1 ? "as" : "a"}{" "}
            <strong>
              {stats.pendentes} {stats.pendentes > 1 ? "questões" : "questão"}{" "}
              {stats.pendentes > 1 ? "não respondidas" : "não respondida"}
            </strong>{" "}
            no seu ritmo médio (
            {stats.tempoMedioRespondidasMs != null
              ? formatHMS(stats.tempoMedioRespondidasMs)
              : "—"}
            ).
          </p>
        </div>
      )}

      {/* Se não há pendentes, ocupa o terceiro slot com "todas respondidas" */}
      {stats.pendentes === 0 && (
        <div class="flex flex-col items-center justify-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-center text-emerald-700 dark:text-emerald-300">
          <CheckCircle className="size-6" />
          <strong class="text-sm font-bold">Todas as questões respondidas</strong>
          <p class="text-[11px] opacity-80">sem tempo extra estimado</p>
        </div>
      )}
    </section>
  );
}

function MetaRow({
  icon: Icon,
  label,
  value,
  hint,
  tone
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  hint?: string | null;
  tone?: "emerald" | "red" | "primary";
}) {
  const valueTone =
    tone === "emerald"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "red"
        ? "text-red-600 dark:text-red-400"
        : tone === "primary"
          ? "text-primary"
          : "text-foreground";
  return (
    <li class="flex items-center gap-3 py-2 first:pt-1 last:pb-1">
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <div class="min-w-0 flex-1">
        <div class="text-[12px] font-medium text-foreground/85">{label}</div>
        {hint && (
          <div class="text-[10.5px] text-muted-foreground/80">{hint}</div>
        )}
      </div>
      <div class={`shrink-0 tabular-nums text-sm font-bold ${valueTone}`}>
        {value}
      </div>
    </li>
  );
}

// =====================
// Breakdown por Tema/Fonte/Dificuldade — abas
// =====================

function ResultBreakdownTabs({
  temas,
  fontes,
  dif
}: {
  temas: BreakdownRow[];
  fontes: BreakdownRow[];
  dif: BreakdownRow[];
}) {
  const [tab, setTab] = useState<"tema" | "fonte" | "dif">("tema");
  const rows = tab === "tema" ? temas : tab === "fonte" ? fontes : dif;
  const tabAvailable = {
    tema: temas.length > 0,
    fonte: fontes.length > 0,
    dif: dif.length > 0
  };
  if (!tabAvailable.tema && !tabAvailable.fonte && !tabAvailable.dif) {
    return null;
  }
  // Auto-fallback se a aba ativa não tem dados.
  const effective = tabAvailable[tab]
    ? tab
    : tabAvailable.tema
      ? "tema"
      : tabAvailable.fonte
        ? "fonte"
        : "dif";
  const effectiveRows =
    effective === "tema" ? temas : effective === "fonte" ? fontes : dif;
  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 px-4 py-3">
      <header class="flex items-center gap-2">
        <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Desempenho por
        </h3>
        <div class="flex items-center gap-1 rounded-md border border-border bg-background/50 p-0.5">
          <TabPill
            active={effective === "tema"}
            disabled={!tabAvailable.tema}
            onClick={() => setTab("tema")}
          >
            Tema
          </TabPill>
          <TabPill
            active={effective === "fonte"}
            disabled={!tabAvailable.fonte}
            onClick={() => setTab("fonte")}
          >
            Fonte
          </TabPill>
          <TabPill
            active={effective === "dif"}
            disabled={!tabAvailable.dif}
            onClick={() => setTab("dif")}
          >
            Dificuldade
          </TabPill>
        </div>
        <span class="ml-auto text-[10px] text-muted-foreground/70">
          piores primeiro
        </span>
      </header>
      <div class="space-y-2">
        {effectiveRows.map((r) => (
          <BreakdownRowItem key={r.label} row={r} />
        ))}
      </div>
    </section>
  );
}

function TabPill({
  active,
  disabled,
  onClick,
  children
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: any;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      class={`rounded px-2 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        active
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:bg-accent hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function BreakdownRowItem({ row }: { row: BreakdownRow }) {
  const respondidasPct =
    row.total > 0
      ? Math.min(100, ((row.acertos + row.erros) / row.total) * 100)
      : 0;
  const acertosPct =
    row.total > 0 ? Math.min(100, (row.acertos / row.total) * 100) : 0;
  return (
    <div class="space-y-1">
      <div class="flex items-baseline gap-2">
        <span class="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
          {row.label}
        </span>
        {/* Nota média (0-10) — coluna ao lado de % de acerto pra mostrar
            os 2 indicadores juntos. Karol/Luís 2026-05-24. */}
        {row.notaMedia != null && (
          <span
            class={`shrink-0 rounded-md px-1.5 py-0.5 tabular-nums text-[11px] font-bold ${
              row.notaMedia >= 7
                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                : row.notaMedia >= 5
                  ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                  : "bg-red-500/15 text-red-600 dark:text-red-400"
            }`}
            title={`Nota média (escala 0–10, ${row.notasComputadas}/${row.total} avaliadas)`}
          >
            {formatNota(row.notaMedia)}
          </span>
        )}
        <span class="shrink-0 tabular-nums text-sm font-bold text-foreground">
          {row.pct != null ? `${row.pct}%` : "—"}
        </span>
      </div>
      <div class="relative h-2 overflow-hidden rounded-full bg-border/60">
        <div
          class="absolute inset-y-0 left-0 bg-primary/35 transition-all"
          style={{ width: `${respondidasPct}%` }}
        />
        <div
          class="absolute inset-y-0 left-0 bg-primary transition-all"
          style={{ width: `${acertosPct}%` }}
        />
      </div>
      <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
        <span>
          {row.acertos + row.erros}/{row.total} respondidas
        </span>
        <span class="opacity-60">·</span>
        <span class="text-emerald-700 dark:text-emerald-400">
          {row.acertos} acerto{row.acertos !== 1 ? "s" : ""}
        </span>
        {row.erros > 0 && (
          <>
            <span class="opacity-60">·</span>
            <span class="text-red-600 dark:text-red-400">
              {row.erros} erro{row.erros !== 1 ? "s" : ""}
            </span>
          </>
        )}
        {row.tempoTotalMs > 0 && (
          <>
            <span class="opacity-60">·</span>
            <span class="inline-flex items-center gap-1 whitespace-nowrap tabular-nums">
              <Clock width="0.9em" height="0.9em" />
              {formatHMS(row.tempoTotalMs)} total
            </span>
          </>
        )}
        {row.tempoMedioMs != null && (
          <>
            <span class="opacity-60">·</span>
            <span class="tabular-nums">
              média {formatHMS(row.tempoMedioMs)}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// =====================
// Tabela de questões com filtros + clique abre gabarito
// =====================

function ResultQuestionsTable({
  state,
  escopo
}: {
  state: ProvaResultState;
  escopo: QuestaoStudy[];
}) {
  const prova = state.prova!;
  const essayResponses = state.essayResponses ?? {};
  const [filtro, setFiltro] = useState<"all" | "correct" | "wrong" | "pending">("all");
  const counts = useMemo(() => {
    let c = 0;
    let w = 0;
    let p = 0;
    for (const q of escopo) {
      const st = statusOf(q, prova, essayResponses);
      if (st === "acerto" || st === "anulada") c++;
      else if (st === "erro") w++;
      else if (st === "respondida") c++; // sem nota IA ainda — conta como respondida
      else p++;
    }
    return { all: escopo.length, c, w, p };
  }, [escopo, prova, essayResponses]);
  const visiveis = useMemo(() => {
    return escopo
      .map((q, idx) => ({ q, idx, st: statusOf(q, prova, essayResponses) }))
      .filter(({ st }) => {
        if (filtro === "all") return true;
        if (filtro === "correct") return st === "acerto" || st === "anulada";
        if (filtro === "wrong") return st === "erro";
        return st === "pendente";
      });
  }, [escopo, prova, filtro]);

  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 px-4 py-3">
      <header class="flex flex-wrap items-center gap-2">
        <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Questões
        </h3>
        <div class="flex flex-wrap items-center gap-1 rounded-md border border-border bg-background/50 p-0.5">
          <TableFilterPill
            active={filtro === "all"}
            onClick={() => setFiltro("all")}
            count={counts.all}
          >
            Todas
          </TableFilterPill>
          <TableFilterPill
            active={filtro === "correct"}
            onClick={() => setFiltro("correct")}
            count={counts.c}
            tone="emerald"
          >
            Acertos
          </TableFilterPill>
          <TableFilterPill
            active={filtro === "wrong"}
            onClick={() => setFiltro("wrong")}
            count={counts.w}
            tone="red"
          >
            Erros
          </TableFilterPill>
          <TableFilterPill
            active={filtro === "pending"}
            onClick={() => setFiltro("pending")}
            count={counts.p}
            tone="muted"
          >
            Pendentes
          </TableFilterPill>
        </div>
        <span class="ml-auto text-[10px] text-muted-foreground/70">
          clique para ver o gabarito
        </span>
      </header>
      {visiveis.length === 0 ? (
        <p class="py-6 text-center text-xs text-muted-foreground">
          Nenhuma questão nesse filtro.
        </p>
      ) : (
        <ul class="divide-y divide-border/60">
          {visiveis.map(({ q, idx, st }) => (
            <QuestionResultRow
              key={q.id}
              q={q}
              idx={idx}
              status={st}
              prova={prova}
              essayResponses={essayResponses}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function TableFilterPill({
  active,
  onClick,
  count,
  tone,
  children
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  tone?: "emerald" | "red" | "muted";
  children: any;
}) {
  const activeCls =
    tone === "emerald"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 shadow-sm"
      : tone === "red"
        ? "bg-red-500/15 text-red-600 dark:text-red-400 shadow-sm"
        : tone === "muted"
          ? "bg-muted text-foreground shadow-sm"
          : "bg-primary text-primary-foreground shadow-sm";
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold transition-colors ${
        active ? activeCls : "text-muted-foreground hover:bg-accent hover:text-foreground"
      }`}
    >
      {children}
      <span class="rounded bg-background/40 px-1 text-[10px] tabular-nums opacity-80">
        {count}
      </span>
    </button>
  );
}

function QuestionResultRow({
  q,
  idx,
  status,
  prova,
  essayResponses
}: {
  q: QuestaoStudy;
  idx: number;
  status: QuestaoStatus;
  prova: ProvaState;
  essayResponses: Record<string, EssayResponseState>;
}) {
  const resposta = prova.respostas[q.id];
  const corretaIdx = (Number(q.correta) || 0) - 1;
  const tempoQ = prova.tempoPorQuestao?.[q.id] ?? resposta?.tempoMs ?? 0;
  const letras = ["A", "B", "C", "D", "E", "F"];
  // Nota da questão — escala 0-10 binária pra MC (acerto=10, erro=0) e
  // ponderada pra essay/composta (nota IA). Karol/Luís 2026-05-24.
  const notaQ = notaQuestaoFinal(q, prova, essayResponses);
  const isEssayOrComposite =
    q.tipo === "essay" || (Array.isArray(q.items) && q.items.length > 0);

  // Coluna 4 — texto da resposta
  let respostaCell: any;
  if (status === "pendente") {
    respostaCell = (
      <span class="text-muted-foreground italic">Não respondida</span>
    );
  } else if (status === "anulada") {
    respostaCell = (
      <span class="text-amber-600 dark:text-amber-400">
        Anulada · contabilizada como acerto
      </span>
    );
  } else if (isEssayOrComposite) {
    // Essay/composta — texto livre. Mostra "Respondida" + indicação se
    // tem nota parcial (composta com items sem nota IA).
    respostaCell = (
      <span class="text-muted-foreground">
        Dissertativa · clique pra ver
        {status === "respondida" && (
          <span class="ml-1 italic">(aguardando nota IA)</span>
        )}
      </span>
    );
  } else {
    respostaCell = (
      <span>
        Sua resposta:{" "}
        <strong
          class={
            status === "acerto"
              ? "text-emerald-700 dark:text-emerald-400"
              : "text-red-600 dark:text-red-400"
          }
        >
          {resposta?.opcao != null ? letras[resposta.opcao] : "—"}
        </strong>
        <span class="mx-1 opacity-50">·</span>
        Correta:{" "}
        <strong class="text-emerald-700 dark:text-emerald-400">
          {corretaIdx >= 0 ? letras[corretaIdx] : "—"}
        </strong>
      </span>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => (gabaritoQuestionId.value = q.id)}
        class="flex w-full items-start gap-3 py-2.5 text-left transition-colors hover:bg-accent/40"
      >
        {/* Col 1 — número + badge status + nota */}
        <div class="flex shrink-0 items-center gap-2">
          <span class="grid w-9 place-items-center rounded-md border border-border bg-background tabular-nums text-xs font-bold">
            {idx + 1}
          </span>
          <StatusBadge status={status} />
          {notaQ != null && (
            <span
              class={`hidden shrink-0 rounded-md px-1.5 py-0.5 tabular-nums text-[11px] font-bold sm:inline-block ${
                notaQ >= 7
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                  : notaQ >= 5
                    ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                    : "bg-red-500/15 text-red-600 dark:text-red-400"
              }`}
              title="Nota da questão (0–10)"
            >
              {formatNota(notaQ)}
            </span>
          )}
        </div>
        {/* Col 2 — tema + dificuldade flat (sem chip) + fonte/#original */}
        <div class="hidden min-w-[120px] max-w-[160px] flex-col gap-0.5 sm:flex">
          {q.tema && (
            <span class="truncate text-xs font-semibold text-primary">
              {q.tema}
            </span>
          )}
          <div class="flex items-center gap-2">
            {q.dificuldade != null && q.dificuldade >= 1 && (
              <FlatStars value={Number(q.dificuldade)} />
            )}
            {q.numero_original != null && (
              <span class="font-mono text-[10px] text-muted-foreground/70">
                #{String(q.numero_original)}
              </span>
            )}
          </div>
        </div>
        {/* Col 3 — preview do enunciado (substitui a fonte que normalmente fica vazia)
            Mostra início da pergunta pra dar contexto e diferenciar as questões na lista. */}
        <div class="hidden min-w-0 flex-1 flex-col gap-0.5 md:flex">
          {q.pergunta && (
            <span class="line-clamp-2 text-xs text-foreground/80">
              {stripMarkdown(q.pergunta)}
            </span>
          )}
          {q.fonte && (
            <span class="truncate text-[10px] text-muted-foreground/70">
              {q.fonte}
            </span>
          )}
        </div>
        {/* Col 4 — resposta */}
        <div class="min-w-0 shrink-0 basis-[200px] text-xs lg:basis-[260px]">
          {respostaCell}
        </div>
        {/* Col 5 — tempo */}
        <div class="hidden shrink-0 tabular-nums text-xs text-muted-foreground sm:block">
          {tempoQ > 0 ? formatHMS(tempoQ) : "—"}
        </div>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground/60" />
      </button>
    </li>
  );
}

function StatusBadge({ status }: { status: QuestaoStatus }) {
  if (status === "acerto") {
    return (
      <span
        title="Acerto"
        class="grid size-6 place-items-center rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
      >
        <Check width="0.95em" height="0.95em" />
      </span>
    );
  }
  if (status === "erro") {
    return (
      <span
        title="Erro"
        class="grid size-6 place-items-center rounded-md bg-red-500/15 text-red-600 dark:text-red-400"
      >
        <XCircle width="0.95em" height="0.95em" />
      </span>
    );
  }
  if (status === "anulada") {
    return (
      <span
        title="Anulada — contabilizada como acerto"
        class="grid size-6 place-items-center rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-400 text-[10px] font-bold"
      >
        ⊗
      </span>
    );
  }
  if (status === "respondida") {
    return (
      <span
        title="Respondida — aguardando ou sem nota IA"
        class="grid size-6 place-items-center rounded-md bg-primary/15 text-primary"
      >
        <Check width="0.95em" height="0.95em" />
      </span>
    );
  }
  return (
    <span
      title="Não respondida"
      class="grid size-6 place-items-center rounded-md bg-muted text-muted-foreground"
    >
      <MinusCircle width="0.95em" height="0.95em" />
    </span>
  );
}

// =====================
// Modal de gabarito — abre ao clicar numa questão
// =====================

function GabaritoModal() {
  const state = provaResultView.value;
  if (!state || !state.prova) return null;
  const qid = gabaritoQuestionId.value;
  if (!qid) return null;
  const q = state.questoes.find((x) => x.id === qid);
  if (!q) return null;
  const prova = state.prova;
  const resposta = prova.respostas[qid];
  const anulada = isAnuladaQ(q);
  const corretaIdx = (Number(q.correta) || 0) - 1;
  const minhaIdx = resposta?.opcao ?? null;
  const tempoQ = prova.tempoPorQuestao?.[qid] ?? resposta?.tempoMs ?? 0;
  const status = statusOf(q, prova, state.essayResponses ?? {});

  // Pega lista de IDs do escopo na ordem da prova pra navegar prev/next.
  const escopoIds = prova.escopoQuestaoIds.length > 0
    ? prova.escopoQuestaoIds
    : state.questoes.map((x) => x.id);
  const curIdx = escopoIds.findIndex((id) => id === qid);
  const prevId = curIdx > 0 ? escopoIds[curIdx - 1] : null;
  const nextId = curIdx >= 0 && curIdx < escopoIds.length - 1 ? escopoIds[curIdx + 1] : null;

  useEffect(() => {
    const onEsc = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") gabaritoQuestionId.value = null;
      if (ev.key === "ArrowLeft" && prevId) gabaritoQuestionId.value = prevId;
      if (ev.key === "ArrowRight" && nextId) gabaritoQuestionId.value = nextId;
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [prevId, nextId]);

  const alternativas: string[] = Array.isArray(q.alternativas)
    ? q.alternativas
    : [];

  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={() => (gabaritoQuestionId.value = null)}
    >
      <div
        class="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Header */}
        <header class="flex items-center gap-2 border-b border-border px-4 py-3">
          <span class="grid size-9 place-items-center rounded-md border border-border bg-background tabular-nums text-sm font-bold">
            {curIdx + 1}
          </span>
          <StatusBadge status={status} />
          {(() => {
            const nota = notaQuestaoFinal(q, prova, state.essayResponses ?? {});
            const peso = pesoQuestao(q);
            return (
              <>
                {nota != null && (
                  <span
                    class={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-bold tabular-nums ${
                      nota >= 7
                        ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        : nota >= 5
                          ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                          : "border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400"
                    }`}
                    title="Nota da questão (escala 0–10)"
                  >
                    {formatNota(nota)}
                  </span>
                )}
                {/* Peso da questão — só mostra se != 1 (default). */}
                {peso !== 1 && (
                  <span
                    class="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground tabular-nums"
                    title="Peso da questão na nota final"
                  >
                    peso {peso}
                  </span>
                )}
              </>
            );
          })()}
          <div class="min-w-0 flex-1 text-xs text-muted-foreground">
            {q.tema && <span class="font-semibold text-primary">{q.tema}</span>}
            {tempoQ > 0 && (
              <span class="ml-1 inline-flex items-center gap-1 whitespace-nowrap tabular-nums">
                <span class="opacity-60">·</span>
                <Clock width="0.9em" height="0.9em" />
                {formatHMS(tempoQ)}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => (gabaritoQuestionId.value = prevId)}
            disabled={!prevId}
            title="Questão anterior (←)"
            class="rounded-md border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-accent disabled:opacity-30"
          >
            ←
          </button>
          <button
            type="button"
            onClick={() => (gabaritoQuestionId.value = nextId)}
            disabled={!nextId}
            title="Próxima questão (→)"
            class="rounded-md border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-accent disabled:opacity-30"
          >
            →
          </button>
          <button
            type="button"
            onClick={() => (gabaritoQuestionId.value = null)}
            title="Fechar (Esc)"
            class="rounded-md border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-accent"
          >
            ✕
          </button>
        </header>

        {/* Body */}
        <div class="flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {/* Badges */}
          <QuestionBadges q={q} anulada={anulada} />

          {/* Caso/situação */}
          {q.caso && (
            <div class="rounded-md border-l-4 border-sky-500/50 bg-sky-500/5 px-3 py-2 text-sm">
              <div
                dangerouslySetInnerHTML={{ __html: renderBlock(q.caso) }}
              />
            </div>
          )}

          {/* Tabelas */}
          {questaoHasTabelas(q) &&
            Array.isArray(q.tabelas) &&
            q.tabelas.map((t, i) => <QuestionTable key={i} table={t} />)}

          {/* Imagens */}
          {questaoHasImages(q) && (
            <div class="space-y-2">
              {Array.isArray(q.imagens) &&
                q.imagens.map((raw, i) => {
                  const img =
                    typeof raw === "string" ? { url: raw, legenda: undefined } : raw;
                  return (
                    <figure
                      key={i}
                      class="cursor-zoom-in rounded-md border border-border bg-background/40 p-1"
                      onClick={() =>
                        (lightboxImg.value = {
                          url: img.url,
                          legenda: img.legenda
                        })
                      }
                    >
                      <img
                        src={img.url}
                        alt={img.legenda || ""}
                        class="mx-auto max-h-[260px] rounded"
                      />
                      {img.legenda && (
                        <figcaption class="mt-1 text-center text-[11px] italic text-muted-foreground">
                          {img.legenda}
                        </figcaption>
                      )}
                    </figure>
                  );
                })}
              {q.imagem_url && !Array.isArray(q.imagens) && (
                <figure
                  class="cursor-zoom-in rounded-md border border-border bg-background/40 p-1"
                  onClick={() =>
                    (lightboxImg.value = { url: q.imagem_url as string })
                  }
                >
                  <img
                    src={q.imagem_url}
                    alt=""
                    class="mx-auto max-h-[260px] rounded"
                  />
                </figure>
              )}
            </div>
          )}

          {/* Pergunta */}
          {q.pergunta && (
            <div
              class="text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: renderBlock(q.pergunta) }}
            />
          )}

          {/* Respostas dissertativas. Karol 2026-05-24: modal mostrava
              "Não respondida" mesmo a aluna tendo escrito. Renderiza:
              - Simple essay (q.tipo === "essay"): 1 bloco com texto + nota
              - Composta (q.items[]): 1 bloco POR item com label + texto +
                gabarito_modelo e nota individual (se já corrigido) */}
          {(q.tipo === "essay" ||
            (Array.isArray(q.items) && q.items.length > 0)) && (
            <EssayAnswersInModal q={q} state={state} />
          )}

          {/* Alternativas.
              Estados visuais:
                - Acertou (sua = correta): verde + "Você acertou"
                - Errou (correta marcada): verde + "Correta"
                - Errou (sua, errada): vermelho + "Sua resposta"
                - Não respondida (correta marcada): roxo + "Correta · Não respondida"
                  (espelha o destaque do modo prova ativo — vide feedback do usuário 2026-05-15)
                - Anulada: todas as alternativas neutras (não há correta)
          */}
          <ol class="space-y-1.5">
            {alternativas.map((alt, i) => {
              const naoRespondida = status === "pendente";
              const isCorreta = !anulada && i === corretaIdx;
              const isMinha = !naoRespondida && i === minhaIdx;
              const isErrada = isMinha && !isCorreta && !anulada;
              const isCorretaNaoRespondida = naoRespondida && isCorreta;
              const bg = isCorretaNaoRespondida
                ? "border-primary/50 bg-primary/10"
                : isCorreta
                  ? "border-emerald-500/50 bg-emerald-500/10"
                  : isErrada
                    ? "border-red-500/50 bg-red-500/10"
                    : "border-border bg-background/30";
              return (
                <li
                  key={i}
                  class={`flex items-start gap-2.5 rounded-md border px-3 py-2 ${bg}`}
                >
                  <span
                    class={`grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                      isCorretaNaoRespondida
                        ? "bg-primary text-primary-foreground"
                        : isCorreta
                          ? "bg-emerald-500 text-white"
                          : isErrada
                            ? "bg-red-500 text-white"
                            : "bg-muted text-foreground"
                    }`}
                  >
                    {["A", "B", "C", "D", "E", "F"][i]}
                  </span>
                  <div class="min-w-0 flex-1 text-sm">
                    {alt && typeof alt === "object" && "imagem" in (alt as Record<string, unknown>) ? (
                      <div class="flex flex-col gap-1">
                        <img
                          src={(alt as { imagem: string }).imagem}
                          alt={(alt as { legenda?: string }).legenda ?? "Alternativa"}
                          loading="lazy"
                          class="block max-h-48 w-auto rounded-md border border-border bg-background object-contain"
                        />
                        {(alt as { legenda?: string }).legenda && (
                          <span
                            class="text-[11px] text-muted-foreground"
                            dangerouslySetInnerHTML={{
                              __html: renderInline(
                                (alt as { legenda?: string }).legenda ?? ""
                              )
                            }}
                          />
                        )}
                      </div>
                    ) : (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: renderInline(String(alt ?? ""))
                        }}
                      />
                    )}
                    {isCorretaNaoRespondida && (
                      <span class="ml-2 text-[10px] font-bold uppercase tracking-wider text-primary">
                        Correta · Não respondida
                      </span>
                    )}
                    {!isCorretaNaoRespondida && isCorreta && !isMinha && (
                      <span class="ml-2 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                        Correta
                      </span>
                    )}
                    {isMinha && !isCorreta && !anulada && (
                      <span class="ml-2 text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400">
                        Sua resposta
                      </span>
                    )}
                    {isMinha && isCorreta && (
                      <span class="ml-2 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                        Você acertou
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Resolução */}
          {q.resolucao && (
            <details class="rounded-md border border-border bg-background/40" open>
              <summary class="cursor-pointer select-none px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground">
                Resolução
              </summary>
              <div
                class="px-3 pb-3 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderBlock(q.resolucao) }}
              />
            </details>
          )}

          {/* Explicar com IA — mesma seção do Revisar prova inteira.
              Em acerto: ainda útil pra reforçar conceitos. Em erro/pendente:
              comportamento padrão (didático + conceitos + onde estudar).
              Pedido 2026-05-24: aluno clica na questão na tabela do Resultado
              e já tem a explicação aqui sem precisar entrar em "Revisar prova
              inteira". Reusa AiExplainBlock pra não duplicar prompt. */}
          <AiExplainBlock
            key={qid}
            q={q as any}
            respostaIdx={minhaIdx}
            corretaIdx={corretaIdx}
            status={status as any}
            bankId={state.bankId}
            provaId={state.provaId}
          />
        </div>
      </div>
    </div>
  );
}

// =====================
// Modal de confirmação de Excluir resultado
// =====================

// =====================
// Render das respostas dissertativas dentro do GabaritoModal.
// Simple essay → 1 bloco. Composta (q.items) → 1 bloco por item.
// Mostra texto da aluna + gabarito-modelo + nota IA (se já corrigida).
// "Não respondida" só quando item não tem texto. Karol 2026-05-24.
// =====================

function EssayAnswersInModal({
  q,
  state
}: {
  q: QuestaoStudy;
  state: ProvaResultState;
}) {
  const essayResponses = state.essayResponses ?? {};
  const touched = new Set(
    Array.isArray(state.prova?.essayKeysTouched)
      ? state.prova!.essayKeysTouched
      : []
  );
  function renderEssayBlock(label: string, key: string, gabaritoModelo?: string) {
    const er = essayResponses[key];
    const resposta =
      typeof er?.resposta === "string" && er.resposta.trim().length > 0
        ? er.resposta
        : null;
    const isTouched = touched.has(key);
    const nota = typeof er?.nota === "number" ? er.nota : null;
    const feedback =
      typeof er?.feedback === "string" && er.feedback.trim().length > 0
        ? er.feedback
        : null;
    const respondida = isTouched && resposta != null;
    return (
      <div
        key={key}
        class={`rounded-md border-l-4 px-4 py-3 ${
          respondida
            ? "border-primary/50 bg-primary/5"
            : "border-border bg-background/40"
        }`}
      >
        <div class="mb-2 flex flex-wrap items-center gap-2">
          {label && (
            <span class="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              {label}
            </span>
          )}
          <span
            class={`text-[10px] font-bold uppercase tracking-wider ${
              respondida
                ? "text-primary"
                : "text-muted-foreground"
            }`}
          >
            {respondida ? "Sua resposta" : "Não respondida"}
          </span>
          {nota != null && (
            <span
              class={`ml-auto rounded-md px-2 py-0.5 text-[11px] font-bold tabular-nums ${
                nota >= 7
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                  : nota >= 5
                    ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                    : "bg-red-500/15 text-red-600 dark:text-red-400"
              }`}
            >
              Nota IA: {nota.toFixed(1).replace(".", ",")} / 10
            </span>
          )}
        </div>
        {respondida ? (
          <div class="whitespace-pre-wrap rounded-md bg-background/60 px-3 py-2 text-sm leading-relaxed">
            {resposta}
          </div>
        ) : (
          <div class="text-xs italic text-muted-foreground">
            (em branco)
          </div>
        )}
        {feedback && (
          <details class="mt-2 rounded-md border border-border bg-background/40">
            <summary class="cursor-pointer select-none px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground">
              Feedback da IA
            </summary>
            <div class="whitespace-pre-wrap px-3 pb-2 text-xs leading-relaxed">
              {feedback}
            </div>
          </details>
        )}
        {gabaritoModelo && (
          <details class="mt-2 rounded-md border border-emerald-500/30 bg-emerald-500/5">
            <summary class="cursor-pointer select-none px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-800 dark:text-emerald-300">
              Gabarito-modelo
            </summary>
            <div class="whitespace-pre-wrap px-3 pb-2 text-xs leading-relaxed text-emerald-900 dark:text-emerald-100">
              {gabaritoModelo}
            </div>
          </details>
        )}
      </div>
    );
  }
  // Composta
  if (Array.isArray(q.items) && q.items.length > 0) {
    return (
      <div class="space-y-2">
        {q.items.map((it, i) => {
          const k = it && it.id ? String(it.id) : String(q.id);
          const label = it?.label ?? String.fromCharCode(97 + i); // "a", "b", ...
          // Item pode ter pergunta própria (sub-comando) — renderiza acima
          // da resposta pra deixar claro QUAL item está sendo respondido.
          return (
            <div key={k} class="space-y-1.5">
              {it?.pergunta && (
                <div
                  class="text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: renderBlockText(it.pergunta)
                  }}
                />
              )}
              {renderEssayBlock(label, k, it?.gabarito_modelo ?? undefined)}
            </div>
          );
        })}
      </div>
    );
  }
  // Simple essay
  return (
    <div class="space-y-2">
      {renderEssayBlock("", String(q.id), (q as any).gabarito_modelo ?? undefined)}
    </div>
  );
}

// Local helper — não usar renderBlock importado pra evitar dependência
// circular (modal já está em escopo profundo). Suporta markdown básico
// inline pra item.pergunta.
function renderBlockText(s: string): string {
  return String(s ?? "")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>");
}

function DeleteConfirmModal() {
  const state = provaResultView.value;
  if (!state) return null;
  async function confirmDelete() {
    deletingProva.value = true;
    try {
      await api.hideProva(state!.bankId, state!.provaId);
      deletingProva.value = false;
      deleteConfirmOpen.value = false;
      const bankId = state!.bankId;
      exitProvaResultView();
      // Volta pro detalhe do banco com dados frescos em vez de bater na home.
      // Sem refresh, o card do resultado deletado ficaria fantasma na lista
      // (incidente Karol 2026-05-23). [[refresh-bank-detail-after-delete]]
      const { activeBankId, refreshActiveBank } = await import("./BankDetailView");
      activeBankId.value = bankId;
      window.history.pushState({}, "", `/student/banks/${encodeURIComponent(bankId)}`);
      void refreshActiveBank();
    } catch (err) {
      deletingProva.value = false;
      void openAlert({
        title: "Erro",
        message:
          err instanceof Error
            ? `Não consegui excluir: ${err.message}`
            : "Não consegui excluir o resultado."
      });
    }
  }
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={() => (deleteConfirmOpen.value = false)}
    >
      <div
        class="w-full max-w-sm space-y-3 rounded-lg border border-border bg-card p-5 shadow-2xl"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div class="flex items-center gap-2">
          <Trash2 className="size-5 text-red-600 dark:text-red-400" />
          <h2 class="text-base font-bold">Excluir este resultado?</h2>
        </div>
        <p class="text-sm text-muted-foreground">
          O resultado vai sumir do seu histórico e da home. O professor/admin ainda
          consegue auditar a tentativa se necessário. Você pode iniciar uma nova
          simulação a qualquer momento.
        </p>
        <div class="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={deletingProva.value}
            onClick={() => (deleteConfirmOpen.value = false)}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={deletingProva.value}
            onClick={confirmDelete}
            class="inline-flex items-center gap-1.5 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-500/15 disabled:opacity-50 dark:text-red-400"
          >
            {deletingProva.value && (
              <Loader2 className="size-3.5 animate-spin" />
            )}
            Excluir resultado
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================
// Footer com Home / Nova simulação
// =====================

function ResultFooter({ state }: { state: ProvaResultState }) {
  const escopo = state.prova
    ? state.prova.escopoQuestaoIds.length > 0
      ? state.prova.escopoQuestaoIds
      : state.questoes.map((q) => q.id)
    : [];
  const firstQid = escopo[0] ?? null;
  return (
    <footer class="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card/40 px-4 py-3">
      <button
        type="button"
        onClick={() => {
          exitProvaResultView();
          window.history.pushState({}, "", "/student/");
          window.dispatchEvent(new PopStateEvent("popstate"));
        }}
        class="flex items-center gap-1.5 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
      >
        <Home width="1.3em" height="1.3em" />
        Voltar para a home
      </button>
      <div class="flex flex-wrap items-center gap-2">
        {firstQid && (
          <button
            type="button"
            onClick={() => {
              if (!state.bank) return;
              // Abre a Revisão em tela cheia (modo navegação com sidebar/mapa).
              // O modal de gabarito continua disponível clicando numa linha
              // da tabela — pra inspeção pontual de uma questão.
              exitProvaResultView();
              enterProvaRevisaoView(state.bankId, state.provaId);
            }}
            class="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
          >
            <Eye width="1.3em" height="1.3em" />
            Revisar prova inteira
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            if (!state.bank) return;
            exitProvaResultView();
            openProvaStartModal(state.bank.id, state.questoes);
            const url = `/student/banks/${encodeURIComponent(
              state.bank.id
            )}/prova/andamento`;
            window.history.pushState({ provaBankId: state.bank.id }, "", url);
          }}
          class="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:brightness-110"
        >
          <RotateCcw width="1.3em" height="1.3em" />
          Nova simulação
        </button>
      </div>
    </footer>
  );
}

// =====================
// StatCard reaproveitado
// =====================

function StatCard({
  label,
  value,
  subline,
  tone,
  icon: Icon
}: {
  label: string;
  value: number | string;
  subline?: string;
  tone: "emerald" | "red" | "muted" | "primary";
  icon: typeof CheckCircle;
}) {
  const toneBg =
    tone === "emerald"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : tone === "red"
        ? "bg-red-500/10 text-red-600 dark:text-red-400"
        : tone === "primary"
          ? "bg-primary/10 text-primary"
          : "bg-muted/40 text-muted-foreground";
  return (
    <div class={`rounded-md border border-border px-3 py-2.5 ${toneBg}`}>
      <div class="flex items-center gap-1.5">
        <Icon width="1.1em" height="1.1em" />
        <div class="text-[10px] font-bold uppercase tracking-wider opacity-80">
          {label}
        </div>
      </div>
      <div class="mt-1 text-2xl font-bold tabular-nums text-foreground">
        {value}
      </div>
      {subline && (
        <div class="mt-0.5 text-[10.5px] opacity-70">{subline}</div>
      )}
    </div>
  );
}

// Estrelinhas de dificuldade sem o pill background (versão "flat") — pra
// usar embutida em rows da tabela, onde o chip do DifficultyStars pesa demais.
function FlatStars({ value }: { value: number }) {
  const n = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <span
      title={`Dificuldade ${n}/5`}
      class="inline-flex items-center gap-px"
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <svg
          key={i}
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill={i < n ? "#f59e0b" : "none"}
          stroke={i < n ? "#f59e0b" : "currentColor"}
          stroke-width="2.5"
          stroke-linejoin="round"
          stroke-linecap="round"
          class={i < n ? "" : "text-muted-foreground/40"}
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
}

// Remove marcadores markdown simples pra preview de uma linha (negrito,
// itálico, links, código inline, headers). Não chama renderBlock aqui porque
// queremos texto puro/leve, não HTML.
function stripMarkdown(raw: string): string {
  return String(raw)
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`[^`]+`/g, (m) => m.slice(1, -1))
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^#+\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

function formatDate(d: Date): string {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}
